import { useStudentBadgeData } from '../../hooks/useStudentBadgeData';
import { Award, Gift, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';

const ITEMS_PER_PAGE = 10;

export const ActivityTimeline = () => {
  const { activities } = useStudentBadgeData();
  const [currentPage, setCurrentPage] = useState(1);
  
  const totalPages = Math.ceil(activities.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const visibleActivities = activities.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  if (activities.length === 0) {
    return (
      <div className="text-center p-6 bg-gray-100 rounded-lg">
        <p className="text-gray-600">No activity recorded yet</p>
      </div>
    );
  }

  const formatDate = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) {
      return `${days} day${days !== 1 ? 's' : ''} ago`;
    } else if (hours > 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else if (minutes > 0) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Recent Activity</h2>
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <span>Page {currentPage} of {totalPages}</span>
          <div className="flex space-x-1">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
      
      <div className="flow-root">
        <ul className="-mb-8">
          {visibleActivities.map((activity, activityIdx) => (
            <li key={activity.id}>
              <div className="relative pb-8">
                {/* Line connecting timeline items */}
                {activityIdx !== visibleActivities.length - 1 ? (
                  <span
                    className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200"
                    aria-hidden="true"
                  />
                ) : null}
                
                <div className="relative flex items-start space-x-3">
                  {/* Activity icon */}
                  <div className={`
                    relative flex items-center justify-center h-10 w-10 rounded-full 
                    ${activity.type === 'award' 
                      ? 'bg-indigo-100 text-indigo-600' 
                      : 'bg-yellow-100 text-yellow-600'
                    }
                  `}>
                    {activity.type === 'award' ? (
                      <Award className="h-5 w-5" />
                    ) : (
                      <Gift className="h-5 w-5" />
                    )}
                  </div>
                  
                  {/* Activity content */}
                  <div className="min-w-0 flex-1">
                    <div>
                      <div className="text-sm">
                        <span className="font-medium text-gray-900">
                          {activity.type === 'award' && activity.awardedBy
                            ? `${activity.awardedBy.name}`
                            : 'You'}{' '}
                          {activity.type === 'award'
                            ? 'awarded'
                            : 'redeemed'}{' '}
                        </span>
                      </div>
                      <p className="mt-0.5 text-sm text-gray-500">
                        {formatDate(activity.timestamp)}
                      </p>
                    </div>
                    <div className="mt-2 text-sm text-gray-700">
                      <p>
                        <span className="font-bold text-indigo-600">
                          +{activity.amount} XP
                        </span>{' '}
                        for {activity.reason}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
      
      <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
        <div className="text-sm text-gray-500">
          Showing {startIndex + 1} to {Math.min(startIndex + ITEMS_PER_PAGE, activities.length)} of {activities.length} activities
        </div>
        <div className="flex space-x-2">
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
            <button
              key={pageNum}
              onClick={() => setCurrentPage(pageNum)}
              className={`px-3 py-1 rounded ${
                pageNum === currentPage
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {pageNum}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};